import { Component, Host, h, State, Prop, Element } from '@stencil/core';
import { GoogleMapsLoader } from '../../utils/services/googleMapsLoader';
import { mapService } from '../../utils/services/mapService';
import { loadingBarService } from '../../utils/services/loadingBarService';
import { initColors, ComponentColors } from '../../utils/constants/colors';
import { searchService } from '../../utils/services/searchService';
import { z } from 'zod';
import { translationService, TranslationsProps } from '../../utils/services/translationService';

export const ASL_SearchBarProps = z.object({
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),
});
export type ASL_SearchBarProps = z.infer<typeof ASL_SearchBarProps>;

@Component({
  tag: 'aico-store-locator-search-bar',
  styleUrl: 'aico-store-locator-search-bar.css',
  shadow: true,
})
export class AicoStoreLocatorSearchBar {
  private autocomplete?: google.maps.places.Autocomplete;
  private inputEl?: HTMLInputElement;
  private unsubscribeController!: AbortController;
  /**
   * The initial center.
   */
  @Prop() initialCenter: google.maps.LatLngLiteral = { lat: 47.3769, lng: 8.5417 };

  /**
   * When true, the geolocation (pin) button is hidden.
   */
  @Prop() disableLocationPin?: boolean = false;

  @State() addressText: string = '';

  @Prop() componentColors?: ComponentColors;
  @Element() el!: HTMLElement;

  @Prop() props?: ASL_SearchBarProps;

  async componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_SearchBarProps.parse(this.props);
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-search-bar');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }

    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();
  }

  private t(key: string, locale?: string): string {
    return translationService.getTranslation('aico-store-locator-search-bar', key, locale);
  }

  async componentDidLoad() {
    await GoogleMapsLoader.getInstance().load();
    this.initAutocomplete();
    searchService.subscribe((search: string) => {
      this.addressText = search;
    }, { signal: this.unsubscribeController.signal });
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
  }

  private initAutocomplete() {
    if (!this.inputEl) return;
    this.autocomplete = new google.maps.places.Autocomplete(this.inputEl, {
      fields: ['geometry', 'formatted_address'],
    });
    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete?.getPlace();
      if (!place || !place.geometry) return;
      this.addressText = place.formatted_address || '';
      const lat = place.geometry.location?.lat() || this.initialCenter.lat;
      const lng = place.geometry.location?.lng() || this.initialCenter.lng;
      mapService.setCenter({ lat, lng });
      searchService.setSearch(this.addressText);
    });
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!this.addressText) return;
      const autoCompleteService = new google.maps.places.AutocompleteService();
      autoCompleteService.getPlacePredictions({ input: this.addressText }, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
          const firstPrediction = predictions[0];
          // Using a dummy div to create the PlacesService instance
          const placesService = new google.maps.places.PlacesService(document.createElement('div'));
          placesService.getDetails({ placeId: firstPrediction.place_id, fields: ['geometry', 'formatted_address'] }, (place, status2) => {
            if (status2 === google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
              this.addressText = place.formatted_address || '';
              const lat = place.geometry.location?.lat() || this.initialCenter.lat;
              const lng = place.geometry.location?.lng() || this.initialCenter.lng;
              mapService.setCenter({ lat, lng });
              searchService.setSearch(this.addressText);
            }
          });
        }
      });
    }
  }

  private handleGeolocationClick() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.addressText = '';
        mapService.setCenter({ lat, lng });
      },
      (err) => {
        loadingBarService.setState('error');
        loadingBarService.resetError(2000);
        console.warn('User denied or error in geolocation:', err);
      }
    );
  }

  render() {
    return (
      <Host>
        <div class="flex items-center bg-white shadow-md px-4 py-2 rounded-full">
          <input
            ref={(el) => (this.inputEl = el as HTMLInputElement)}
            type="text"
            placeholder={this.t('City')}
            value={this.addressText}
            onInput={(e: Event) => {
              this.addressText = (e.target as HTMLInputElement).value;
              if (this.addressText.length === 0) {
                searchService.setSearch('');
              }
            }}
            onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e)}
            class="flex-1 pl-2 border-none rounded-full focus:outline-none text-gray-700 placeholder-gray-400"
          />
          {!this.disableLocationPin && (
            <button
              type="button"
              onClick={() => this.handleGeolocationClick()}
              class="bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] ml-1 p-2 rounded-full focus:outline-none h-full text-[--tertiary-text-color] hover:text-[--primary-text-color] transition-colors"
              title="Use my current location"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="0"
                class="w-6 h-6"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 3.68 3.71 8.76 6.34 12.24.32.41.92.41 1.24 0C15.29 17.76 19 12.68 19 9c0-3.87-3.13-7-7-7zm0 10.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7.5 12 7.5s2.5 1.12 2.5 2.5S13.38 12.5 12 12.5z" />
              </svg>
            </button>
          )}
        </div>
      </Host>
    );
  }
}
